import React, {useState, useEffect, useCallback} from 'react';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import Image from 'react-bootstrap/Image';
import Dropdown from 'react-bootstrap/Dropdown';
import { usePaywallOptional } from '../paywall/PaywallProvider.js';
import { navigateToLearnMoreRenew, navigateToLearnMoreJoin } from '../paywall/learnMoreNavigation.js';
import {
  getMembershipCta,
  shouldShowMembershipDropdownItems,
} from '../paywall/accessDisplay.js';
import {
  startMembershipCheckoutFromLocation,
} from '../paywall/joinCheckoutFlow.js';
import { isTeacherAdminNavRoute } from '../../themes/teacherViewRollout.js';
import TentTopMark from '../infrastructure/sharedComponents/TentTopMark.js';
import './navbar.component.styles.scss';

const defaultAvatar = "data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Ccircle cx='20' cy='20' r='20' fill='%23808080'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='0.3em' font-size='24' fill='white'%3E👤%3C/text%3E%3C/svg%3E";

function getManageClassesNavClass(pathname) {
  return [
    'nav-text',
    'mx-3',
    'd-flex',
    'align-items-center',
    isTeacherAdminNavRoute(pathname) ? 'active' : '',
  ].filter(Boolean).join(' ');
}

export default function Navigation({isAuthenticated, username, avatarUrl, userRoles, userId}) {
  const navigate = useNavigate();
  const location = useLocation();
  const paywall = usePaywallOptional();
  const access = paywall?.access;
  const refreshAccess = paywall?.refreshAccess;
  const [imageError, setImageError] = useState(false);
  const [membershipCheckoutLoading, setMembershipCheckoutLoading] = useState(false);

  const navbarMembershipCta = isAuthenticated ? getMembershipCta(access, 'navbar') : null;
  const menuMembershipCta = isAuthenticated ? getMembershipCta(access, 'menu') : null;

  useEffect(() => {
    setImageError(false);
  }, [avatarUrl]);

  const createFallbackAvatar = () => {
    if (username) {
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(username)}&background=0d6efd&color=fff&size=40`;
    }
    return defaultAvatar;
  };

  const profileImage = (avatarUrl && !imageError) ? avatarUrl : createFallbackAvatar();
  
  const handleImageError = () => {
    fetch('https://accounts.google.com/favicon.ico', { mode: 'no-cors' })
      .then(() => console.log("✅ Navbar: Can reach Google services"))
      .catch(() => console.log("❌ Navbar: Cannot reach Google services"));
      
    setImageError(true);
  };

  const isTeacher = userRoles?.teacher === true;
  const isSiteAdmin = userRoles?.siteAdmin === true;

  const handleMembershipCta = useCallback(async (cta) => {
    if (!cta || membershipCheckoutLoading) {
      return;
    }

    setMembershipCheckoutLoading(true);
    try {
      await startMembershipCheckoutFromLocation({
        location,
        refreshAccess,
        intent: cta.mode,
      });
    } catch (error) {
      console.error('Membership checkout failed:', error);
      if (error.code === 'EMAIL_VERIFICATION_REQUIRED') {
        navigate('/viewProfile');
        return;
      }
      navigate('/learn-more', { state: { from: `${location.pathname}${location.search}` } });
    } finally {
      setMembershipCheckoutLoading(false);
    }
  }, [location, membershipCheckoutLoading, navigate, refreshAccess]);

  const handleLearnMore = useCallback((mode) => {
    if (mode === 'renew') {
      navigateToLearnMoreRenew(navigate, location);
      return;
    }
    navigateToLearnMoreJoin(navigate, location);
  }, [location, navigate]);

  const renderNavbarMembershipCta = () => {
    if (!navbarMembershipCta) {
      return null;
    }

    const { label, loadingLabel } = navbarMembershipCta;

    return (
      <button
        type="button"
        className="nav-text paywall-membership-nav-link me-2 btn btn-link p-0 border-0 align-self-center"
        onClick={() => handleMembershipCta(navbarMembershipCta)}
        disabled={membershipCheckoutLoading}
      >
        {membershipCheckoutLoading ? loadingLabel : label}
      </button>
    );
  };

  const renderMembershipDropdownItems = () => {
    if (!shouldShowMembershipDropdownItems(access) || !menuMembershipCta) {
      return null;
    }

    const { label, loadingLabel } = menuMembershipCta;

    return (
      <>
        <Dropdown.Item
          onClick={() => handleMembershipCta(menuMembershipCta)}
          disabled={membershipCheckoutLoading}
          className="paywall-dropdown-join"
        >
          {membershipCheckoutLoading ? loadingLabel : label}
        </Dropdown.Item>
        <Dropdown.Item
          onClick={() => handleLearnMore(menuMembershipCta.mode)}
          className="paywall-dropdown-learn-more"
        >
          Learn More
        </Dropdown.Item>
      </>
    );
  };

  const renderAuthenticatedDropdownItems = () => (
    <>
      {isSiteAdmin && <Dropdown.Item onClick={() => navigate('/ccAdmin')}>View Site</Dropdown.Item>}
      {isTeacher && <Dropdown.Item href="/manageClasses">Manage Classes</Dropdown.Item>}
      {renderMembershipDropdownItems()}
      <Dropdown.Item onClick={() => navigate(`/userProgress/${userId}`)}>View Progress</Dropdown.Item>
      <Dropdown.Item onClick={() => navigate('/viewProfile')}>View Profile</Dropdown.Item>
      <Dropdown.Item onClick={() => navigate('/logout')}>Logout</Dropdown.Item>
    </>
  );

  const renderUserMenuToggle = (toggleId, showUsername = true) => (
    <Dropdown.Toggle
      variant="link"
      id={toggleId}
      className="navbar-user-menu-toggle border-0 text-decoration-none text-light p-0 d-flex align-items-center"
      style={showUsername ? { fontSize: '0.9rem' } : undefined}
    >
      <Image
        src={profileImage}
        alt=""
        width={40}
        height={40}
        roundedCircle
        className={showUsername ? 'me-2' : 'me-1'}
        onError={handleImageError}
      />
      {showUsername && username ? <span>{username}</span> : null}
    </Dropdown.Toggle>
  );
  
  return (
  <Navbar bg="dark" expand="sm" className="custom-navbar">
        <Container>
          <Navbar.Brand className="nav-text me-3 tent-top-brand" href="/calculusHome" style={{padding: 0}}>
            <TentTopMark />
          </Navbar.Brand>

          <Navbar.Collapse id="basic-navbar-nav" className="flex-grow-1">
            <Nav className="me-auto d-flex align-items-center">
              <NavLink className="nav-text mx-3 d-flex align-items-center" to="/sectionHome">Calculus</NavLink>
              {isTeacher ? (
                <NavLink className={getManageClassesNavClass(location.pathname)} to="/manageClasses">
                  Manage Classes
                </NavLink>
              ) : null}
            </Nav>
            <div className="d-flex align-items-center ms-auto">
              {renderNavbarMembershipCta()}
              <Dropdown className="d-sm-none">
                {renderUserMenuToggle('dropdown-user-mobile', false)}
                <Dropdown.Menu align="end">
                  {isAuthenticated ? (
                    renderAuthenticatedDropdownItems()
                  ) : (
                    <Dropdown.Item href="/login">Sign-in</Dropdown.Item>
                  )}
                </Dropdown.Menu>
              </Dropdown>
              <div className="d-none d-sm-flex align-items-center">
                {isAuthenticated ? (
                    <Dropdown>
                      {renderUserMenuToggle('dropdown-user-desktop', true)}
                      <Dropdown.Menu align="end">
                        {renderAuthenticatedDropdownItems()}
                      </Dropdown.Menu>
                    </Dropdown>
                ): (
                  <Nav.Link href="/loginWithGoogle" className="text-light">Sign-in</Nav.Link>
                )}
              </div>
            </div>
          </Navbar.Collapse>
        </Container>
      </Navbar>
    );
}
